import {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    forwardRef,
    MouseEvent,
    ReactNode,
} from "react";
import {Link} from "react-router";
import classNames from "classnames";
import "./Button.scss";
import { isSafeHref } from "@/widgets/play/safeHref";

/**
 * Button — the canonical DevourPlay button, per the anchor design guide
 * (§6.1 Buttons, §6.2 Icon buttons). One button for the whole app; replaces
 * the retired legacy button and its 20-colour / 7-size sprawl.
 *
 *   primary  solid red — the single accent, one per moment
 *   ghost    outlined secondary on a solid surface
 *   ghost-hero  outlined + blur, over hero art
 *   link     text link (auto trailing arrow)
 *   icon     36×36 neutral icon-only (optional count badge + active)
 *
 * Polymorphic: renders <Link> when `to` is set, <a> when `href` is set,
 * otherwise <button>. Self-contained styling (Button.scss) — no dependency on
 * the tournament `.tournament-ui` token scope, so it works on every surface.
 */

export type ButtonVariant = "primary" | "ghost" | "ghost-hero" | "link" | "icon";

/**
 * Allow-list guard for the `href`/`to` props. Because Button is the canonical
 * app-wide link-button, a caller could wire brand/CMS-supplied data straight
 * into `href`; without this guard a `javascript:`/`data:`/`vbscript:` URI would
 * execute on click (XSS).
 *
 * Delegates to the shared guard (which also rejects protocol-relative `//host`
 * escapes) and widens it by the two schemes only a button needs — a duplicate
 * copy is how the `//` hole survived in six places at once.
 */
function isSafeButtonHref(href: string | undefined): boolean {
    if (!href) return false;
    return isSafeHref(href) || (/^(mailto:|tel:)/i).test(href);
}

interface ButtonProps {
    variant?: ButtonVariant;
    children?: ReactNode;
    /** Leading icon component (rendered before children). */
    leftIcon?: FC<{ className?: string }>;
    /** Trailing icon component (rendered after children). */
    rightIcon?: FC<{ className?: string }>;
    /** Unread/notification count badge — icon variant only. */
    count?: ReactNode;
    /** Persisted active state — icon variant only. */
    active?: boolean;
    className?: string;
    /** react-router internal link → renders <Link>. */
    to?: string;
    /** External link → renders <a>. */
    href?: string;
    /** Anchor target for `href`. Defaults to "_blank" since href is for external links. */
    target?: "_self" | "_blank" | "_parent" | "_top";
    type?: "button" | "submit" | "reset";
    disabled?: boolean;
    /** Show an inline spinner + optional text, and disable the button. */
    loading?: boolean;
    loadingText?: string;
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
    "aria-label"?: string;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(({
    variant = "primary",
    children,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    count,
    active,
    className,
    to,
    href,
    target = "_blank",
    type = "button",
    disabled,
    loading,
    loadingText,
    onClick,
    ...rest
}, ref) => {
    const isDisabled = disabled || loading;

    const cls = classNames("btn", `btn--${variant}`, className, {
        "is-active": active,
        "is-disabled": isDisabled,
    });

    function handleClick(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void {
        if (isDisabled) {
            event.preventDefault();
            return;
        }
        onClick?.(event);
        event.stopPropagation();
    }

    const content: ReactNode = loading
        ? <span className="btn__loading">
            {loadingText && <span className="btn__loading-text">{loadingText}</span>}
            <span className="btn__spinner"/>
        </span>
        : <>
            {LeftIcon && <span className="btn__icon btn__icon--left"><LeftIcon/></span>}
            {children != null && <span className="btn__label">{children}</span>}
            {count != null && <span className="btn__count">{count}</span>}
            {RightIcon && <span className="btn__icon btn__icon--right"><RightIcon/></span>}
        </>;

    if (to && isSafeButtonHref(to) && !isDisabled) {
        return (
            <Link
                {...rest}
                to={to}
                className={cls}
                onClick={handleClick}
                ref={ref as React.Ref<HTMLAnchorElement>}
            >
                {content}
            </Link>
        );
    }
    if (href && isSafeButtonHref(href) && !isDisabled) {
        return (
            <a
                {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
                href={href}
                target={target}
                rel="noopener noreferrer"
                className={cls}
                onClick={handleClick}
                ref={ref as React.Ref<HTMLAnchorElement>}
            >
                {content}
            </a>
        );
    }
    return (
        <button
            {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
            type={type}
            className={cls}
            onClick={handleClick}
            disabled={isDisabled}
            ref={ref as React.Ref<HTMLButtonElement>}
        >
            {content}
        </button>
    );
});

export default Button;
